import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { Search, ArrowRight } from "lucide-react";
import hero3dHouse from "@/assets/hero-3d-house.png";
import { capture } from "@/lib/analytics";
import HeroBanners from "@/components/HeroBanners";

const Hero = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <section className="min-h-[60vh] md:min-h-[85vh] flex items-center overflow-hidden relative pt-16">
      {/* Green gradient mesh background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-background" />
        <div className="absolute top-[-30%] right-[-15%] w-[80%] h-[90%] rounded-full blur-[140px]" style={{ background: 'hsl(152, 55%, 28%, 0.15)' }} />
        <div className="absolute bottom-[-20%] left-[-10%] w-[60%] h-[70%] rounded-full blur-[120px]" style={{ background: 'hsl(160, 45%, 22%, 0.12)' }} />
        <div className="absolute top-[10%] left-[30%] w-[40%] h-[50%] rounded-full blur-[100px]" style={{ background: 'hsl(152, 60%, 40%, 0.08)' }} />
        <div className="absolute bottom-[10%] right-[5%] w-[35%] h-[45%] rounded-full blur-[90px]" style={{ background: 'hsl(38, 92%, 50%, 0.06)' }} />
        <div className="absolute top-[50%] left-[-5%] w-[25%] h-[30%] rounded-full blur-[80px]" style={{ background: 'hsl(152, 55%, 35%, 0.1)' }} />
      </div>

      <div className="container mx-auto px-4 py-16 lg:py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          {/* Left: Content */}
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground mb-6 leading-tight">
              {t.hero.title}
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-8">
              {t.hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="py-4 px-8 rounded-full text-base" onClick={() => { capture('click_search', { source: 'hero' }); navigate("/properties"); }}>
                <Search className="h-5 w-5" />
                {t.hero.ctaPrimary}
              </Button>
              <Button variant="outline" size="lg" className="py-4 px-8 rounded-full text-base" onClick={() => { capture('click_services', { source: 'hero' }); navigate("/services"); }}>
                {t.hero.ctaSecondary}
                <ArrowRight className="h-5 w-5" />
              </Button>
            </div>

            <HeroBanners />
          </div>

          {/* Right: 3D House Visual */}
          <div className="hidden lg:flex items-center justify-center relative">
            <div className="relative w-[480px] h-[480px]">
              {/* Glow background */}
              <div className="absolute inset-[-20%] rounded-full bg-primary/10 blur-[80px] animate-pulse-soft" />

              {/* 3D House Image */}
              <div className="relative z-10 w-full h-full flex items-center justify-center">
                <img
                  src={hero3dHouse}
                  alt="3D smart home illustration"
                  className="w-full h-full object-cover rounded-3xl border border-border/50 shadow-2xl"
                  fetchPriority="high"
                />

                {/* Price evaluation badge */}
                <div className="absolute bottom-4 left-4 right-4 bg-card/95 backdrop-blur-md border border-primary/30 rounded-2xl px-5 py-3 shadow-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">AI Оценка</span>
                    <span className="text-xs font-bold text-primary">Под пазара с 24%</span>
                  </div>
                  <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-primary to-accent" style={{ width: '76%' }} />
                  </div>
                  <div className="flex justify-between mt-1.5">
                    <span className="text-[10px] text-muted-foreground">€0</span>
                    <span className="text-[10px] font-semibold text-primary">€128,000</span>
                    <span className="text-[10px] text-muted-foreground">€168,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
